import './styles/RoleCard.scss';

import { useLazyQuery, useQuery } from '@apollo/client';
import { Help } from '@mui/icons-material';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Icon,
  MenuItem,
  Select,
  TextField,
  Tooltip
} from '@mui/material';
import { OrganizerType } from '@thro-types/events';

import { useFormikContext } from 'formik';
import { intersection } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import AUTHOR_ROLE_QUERY from 'gql/queries/EventService/getPermissions.gql';
import AUTHORS_QUERY from 'gql/queries/PermissionProvider/getAuthors.gql';
import DEPARTMENTS_OF_USER from 'gql/queries/PermissionProvider/getDepartmentsOfUser.gql';
import PERMISSION_QUERY from 'gql/queries/PermissionProvider/getPermission.gql';

import { DefaultAccordionEnum } from 'app/components';
import { FormError } from 'app/components/FormError/FormError';
import GQLGuard from 'app/components/GQLGuard';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { EventUserAutcomplete } from 'app/containers/Events/EventModal/components/EventUserAutocomplete';
import { translations } from 'locales/i18n';

import { RoleArrcordion } from './RecipientAccordion';
import { RoleCard } from './RoleCard';
import SemesterIcon from './SemesterIcon';

interface Props {
  onClose: Function;
}

export function UserGroupNew({ onClose }: Props) {
  const { values, setFieldValue, errors, validateForm, touched, setFieldTouched }: any =
    useFormikContext();
  const [author, setAuthor] = useState(values.postAs ? values.postAs : '');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const authorsQuery = useQuery<any, any>(AUTHORS_QUERY, {
    fetchPolicy: 'no-cache'
  });
  const getAuthorRolesQuery = useQuery(AUTHOR_ROLE_QUERY, { variables: { limit: 0 } });

  const [getPermission, getPermissionQuery] = useLazyQuery(PERMISSION_QUERY, {
    fetchPolicy: 'no-cache'
  });
  const dataPermission = getPermissionQuery.data;
  const departmentsQuery = useQuery<any, any>(DEPARTMENTS_OF_USER);

  const semesterOptions = [
    {
      degree: 'Bachelor',
      options: [
        'Bachelor-1',
        'Bachelor-2',
        'Bachelor-3',
        'Bachelor-4',
        'Bachelor-5',
        'Bachelor-6',
        'Bachelor-7',
        'Bachelor-8',
        'Bachelor-9'
      ]
    },
    { degree: 'Master', options: ['Master-1', 'Master-2', 'Master-3'] }
  ];

  const handleQuerys = authors => {
    if (authors.getAuthors.length === 0) {
      onClose();
      dispatch(
        AlertActions.error(t(translations.pages.articles.create.userGroup.noPossibleAuthor()))
      );
      return;
    }

    if (authors.getAuthors.filter(authorRole => authorRole.id === author).length === 0 && author) {
      setAuthor(authors.getAuthors[0].id);
      dispatch(
        AlertActions.warning(t(translations.pages.articles.create.userGroup.changingPostAs()))
      );
      setFieldValue('recipients', []);
    }

    if (author && getPermissionQuery.data === undefined) {
      getPermission({ variables: { authorId: author } });
    }
  };

  const handleClickRecipient = recipient => {
    if (!touched.recipients) setFieldTouched('recipients');
    let recipients = [...values.recipients] as number[];
    if (recipients.includes(recipient.entitlement)) {
      recipients.splice(recipients.indexOf(recipient.entitlement), 1);
    } else {
      recipients.push(recipient.entitlement);
    }
    setFieldValue('recipients', recipients, true);
  };

  const handleClickSemester = semester => {
    let semesters = [...values.semesters] as string[];
    if (semesters.includes(semester)) {
      semesters.splice(semesters.indexOf(semester), 1);
    } else {
      semesters.push(semester);
    }
    setFieldValue('semesters', semesters);
  };

  const containsStudentEntitlement = (): boolean => {
    return !!dataPermission?.permission.recipientRoles.find(r => {
      return r.groupEntitlements.find(
        ent => values.recipients.includes(ent.entitlement) && ent.entitlement.includes('Studenten')
      );
    });
  };

  const renderRecipients = () => {
    if (dataPermission.permission.recipientRoles.length === 0) {
      return null;
    }
    return (
      <>
        {dataPermission.permission.recipientRoles.map(roleRecipient => {
          const selected = intersection(
            values.recipients,
            roleRecipient.groupEntitlements.map(ent => ent.entitlement)
          ).length;

          return (
            <RoleArrcordion
              title={roleRecipient.name}
              elements={roleRecipient.groupEntitlements}
              type={DefaultAccordionEnum.RECIPIENT}
              amountSelected={selected}
              checked={
                selected === roleRecipient.groupEntitlements.length &&
                values.recipients.length !== 0
              }
              icon={
                <Icon
                  className={roleRecipient.icon}
                  style={{
                    width: '45px',
                    marginRight: '0.25em',
                    textAlign: 'center',
                    color:
                      intersection(
                        values.recipients,
                        roleRecipient.groupEntitlements.map(ent => ent.entitlement)
                      ).length > 0
                        ? '#f39100'
                        : 'grey'
                  }}
                />
              }
              active={false}
            >
              <Grid container>
                {roleRecipient.groupEntitlements.map(recipient => (
                  <RoleCard
                    key={recipient.groupEntitlements?.indexOf(recipient)}
                    object={{ name: recipient.entitlement, icon: roleRecipient.icon }}
                    selected={values.recipients.includes(recipient.entitlement) ? true : false}
                    type="recipient"
                    onClick={() => {
                      handleClickRecipient(recipient);
                    }}
                  />
                ))}
              </Grid>
            </RoleArrcordion>
          );
        })}
      </>
    );
  };

  const renderSemesters = () => {
    return (
      <FormControl className="articleModal__body__input" required fullWidth error={errors.type}>
        <FormLabel className="articleModal__body__input__label">
          {t(translations.pages.articles.create.userGroup.semester())}
        </FormLabel>
        {errors.semesters && <FormError message={errors.semesters} />}
        <Grid container>
          {semesterOptions.map(degree => {
            const selected = intersection(values.semesters, degree.options).length;
            return (
              <RoleArrcordion
                title={degree.degree}
                elements={degree.options}
                type={DefaultAccordionEnum.SEMESTER}
                amountSelected={selected}
                checked={selected >= degree.options.length && values.semesters.length !== 0}
                icon={
                  <SemesterIcon
                    header
                    type={degree.degree === 'Master' ? 'master' : 'bachelor'}
                    active={intersection(values.semesters, degree.options).length > 0}
                  />
                }
                active={intersection(values.semesters, degree.options).length > 0}
              >
                <Grid container>
                  {degree.options.map(semester => (
                    <RoleCard
                      key={degree.options.indexOf(semester)}
                      object={{ name: semester, semester: semester.split('-')[1] }}
                      selected={values.semesters.includes(semester) ? true : false}
                      type="semester"
                      onClick={() => {
                        handleClickSemester(semester);
                      }}
                    />
                  ))}
                </Grid>
              </RoleArrcordion>
            );
          })}
        </Grid>
      </FormControl>
    );
  };

  return (
    <GQLGuard
      query={[authorsQuery, departmentsQuery, getAuthorRolesQuery]}
      lazyQuery={getPermissionQuery}
      onError={error => {
        dispatch(AlertActions.error(`${t(translations.pages.articles.alerts.errorLoading())}`));
        navigate('/articles', { replace: true });
        return null;
      }}
    >
      {([authors, departments, authorRoles]) => {
        handleQuerys(authors);
        const authorOrganizerOptions = authorRoles.permissions?.map(role => ({
          label: role.authorRole.name,
          id: parseInt(role.authorRole.id, 10)
        }));
        return (
          <Grid container>
            <Grid container>
              <FormControl className="articleModal__body__input" fullWidth error={errors.type}>
                {departments.departmentsOfUser.length === 0 ? null : (
                  <>
                    <FormLabel
                      className="articleModal__body__input__label"
                      error={touched.department && errors.department}
                    >
                      {t(translations.pages.articles.create.userGroup.department())}
                    </FormLabel>
                    <Grid container>
                      <Select
                        disabled
                        variant="filled"
                        value={values.department}
                        name="department"
                        onChange={e => {
                          if (!touched.department) {
                            setFieldTouched('department');
                          }
                          setFieldValue('department', e.target.value as string);
                        }}
                        style={{ minWidth: '20em' }}
                        error={touched.department && errors.department}
                        margin="dense"
                      >
                        <MenuItem key={0} value={''}>
                          ---
                        </MenuItem>
                        {departments.departmentsOfUser.map(d => (
                          <MenuItem key={d.id} value={d.id}>
                            {d.title}
                          </MenuItem>
                        ))}
                      </Select>
                      <span style={{ marginLeft: '1em', alignSelf: 'center' }}>
                        {t(translations.layout.menu.temporarilyDisabled())}
                      </span>
                    </Grid>
                    {touched.department && errors.department && (
                      <FormHelperText error>{errors.department}</FormHelperText>
                    )}
                  </>
                )}
                <FormControl className="articleModal__body__input" required fullWidth>
                  <FormLabel className="articleModal__body__input__label">
                    {t(translations.pages.articles.create.userGroup.author())}
                  </FormLabel>
                  {touched.postAs && errors.postAs && <FormError message={errors.postAs} />}
                  <Grid container justifyContent="flex-start" spacing={2}>
                    {authors.getAuthors?.map(postAuthor => (
                      <RoleCard
                        key={postAuthor.id}
                        object={postAuthor}
                        disabled={!!values?.id}
                        selected={author === postAuthor.id ? true : false}
                        type="author"
                        onClick={async () => {
                          setAuthor(postAuthor.id);
                          await Promise.all([
                            setFieldValue('postAs', postAuthor.id),
                            setFieldValue('recipients', []),
                            setFieldValue('semesters', [])
                          ]);
                          await validateForm();
                          getPermission({
                            variables: { id: postAuthor.authorPermission.id }
                          });
                        }}
                      />
                    ))}
                  </Grid>
                </FormControl>
              </FormControl>
            </Grid>
            <Grid container>
              <FormControl className="articleModal__body__input" required fullWidth>
                <FormLabel className="articleModal__body__input__label">
                  {t(translations.pages.articles.create.userGroup.recipients())}
                </FormLabel>
                <Grid container>
                  {dataPermission === undefined ? (
                    <Grid container style={{ padding: '1em 0.5em' }}>
                      <NoItems
                        icon={<Icon className="fas fa-info-circle" />}
                        message={t(translations.pages.articles.create.userGroup.noAuthorSelected())}
                      />
                    </Grid>
                  ) : (
                    <>
                      {dataPermission.permission.recipientRoles.length === 0 ? (
                        <Grid container style={{ padding: '1em 0.5em' }}>
                          <NoItems
                            icon={<Icon className="fas fa-info-circle" />}
                            message={t(translations.pages.articles.create.userGroup.noRecipients())}
                          />
                        </Grid>
                      ) : (
                        <>
                          {touched.recipients && errors.recipients && (
                            <FormError message={errors.recipients} />
                          )}
                          <Grid item xs={12}>
                            <Grid container>{renderRecipients()}</Grid>
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </Grid>
              </FormControl>
            </Grid>
            {containsStudentEntitlement() && renderSemesters()}
            <FormControl fullWidth>
              <Grid container>
                <FormLabel className="articleModal__body__input__label">
                  {t(translations.pages.events.create.organizer())}
                </FormLabel>
                <Grid
                  item
                  container
                  xs
                  justifyContent="flex-end"
                  alignItems="self-end"
                  paddingBottom={'0.5em'}
                >
                  <Tooltip title={t(translations.pages.articles.create.tooltips.organizingRoles())}>
                    <Help fontSize="medium" />
                  </Tooltip>
                </Grid>
              </Grid>
              <Autocomplete
                value={
                  values.organizers &&
                  values.organizers
                    .filter(org => org.organizerType !== OrganizerType.USER)
                    .map(org => authorOrganizerOptions.find(opt => opt.id === org.refId))
                }
                multiple
                id="organizers"
                options={authorOrganizerOptions}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                fullWidth
                onChange={(_, value) => {
                  setFieldValue('organizers', [
                    ...values.organizers.filter(org => org.organizerType !== OrganizerType.ROLE),
                    ...value.map(v => ({
                      refId: v.id,
                      organizerType: OrganizerType.ROLE
                    }))
                  ]);
                }}
                renderInput={params => <TextField {...params} />}
              />
            </FormControl>
            <FormControl fullWidth>
              <FormLabel className="articleModal__body__input__label">
                {t(translations.pages.events.create.organizingPersons())}
              </FormLabel>
              <EventUserAutcomplete />
            </FormControl>
          </Grid>
        );
      }}
    </GQLGuard>
  );
}
